import { cloneDeep } from 'lodash';
import { MLStatus, RewindRecording, SurfedDirection } from 'types/recordings';
import {
  combineWaves,
  generateEmptyWaveGraphBars,
  updateWaveGraphBarsWithNewRewind,
} from './waveInsightData';
import { clipDate } from './clipDate';
import {
  type BaseRewindPlaylistBlock,
  type ClipsPlaylistBlock,
  type RewindError,
  RewindErrorType,
  type RewindPlaylists,
  type SmartRewindPlaylistBlock,
} from './playlistBlocks.types';

/**
 * Playlist blocks are blockDurationInMs minutes long starting from the latest rewind end time
 * There is a potential overlap of overlapDurationInMs between blocks
 * This function, takes the latest rewind end time and a given timestamp and calculates what playlists that timestamp falls into.
 *
 * The Maths is essentially
 * * Find the difference between the two times and dividing by (the length of a playlist block - the overlap duration)
 * * That gives us how many playlist blocks back from the latest one we need to go
 * * we floor it to remove the remainder but also consider the next block over due to the overlap
 * * We then subtract that many playlist blocks times (minus the overlap) from the latest rewind end time
 * * Then we check if that block contains the timestamp and if its does we return it
 */
export const calculateMatchingPlaylistBlock = (
  playlistsEndTimeInMs: number,
  timestampInMs: number,
  blockDurationInMs: number,
  overlapDurationInMs: number = 0,
) => {
  const indexOfMatchingBlock = Math.floor(
    (playlistsEndTimeInMs - timestampInMs) / (blockDurationInMs - overlapDurationInMs),
  );

  const matchingBlockEndTimeInMs =
    playlistsEndTimeInMs - indexOfMatchingBlock * (blockDurationInMs - overlapDurationInMs);
  const matchingBlockStartTimeInMs = matchingBlockEndTimeInMs - blockDurationInMs;

  const potentialMatchingBlockEndTimeInMs =
    playlistsEndTimeInMs - (indexOfMatchingBlock - 1) * (blockDurationInMs - overlapDurationInMs);
  const potentialMatchingBlockStartTimeInMs = potentialMatchingBlockEndTimeInMs - blockDurationInMs;

  const matchingBlocks = [];

  if (
    timestampInMs <= matchingBlockEndTimeInMs &&
    timestampInMs >= matchingBlockStartTimeInMs &&
    matchingBlockEndTimeInMs <= playlistsEndTimeInMs
  ) {
    matchingBlocks.push(new Date(matchingBlockEndTimeInMs).toISOString());
  }

  if (
    timestampInMs <= potentialMatchingBlockEndTimeInMs &&
    timestampInMs >= potentialMatchingBlockStartTimeInMs &&
    potentialMatchingBlockEndTimeInMs <= playlistsEndTimeInMs
  ) {
    matchingBlocks.push(new Date(potentialMatchingBlockEndTimeInMs).toISOString());
  }

  if (matchingBlockEndTimeInMs > playlistsEndTimeInMs)
    // If rewind is after the lateset playlist block, return the latest playlist block (this should never happen)
    matchingBlocks.push(new Date(playlistsEndTimeInMs).toISOString());

  return matchingBlocks;
};

export const getMatchingPlaylistTimesForRewind = (
  rewindStartTime: string,
  rewindEndTime: string,
  playlistsEndTimeInMs: number,
  blockLength: number,
  blockOverlap: number = 0,
) => [
  ...new Set([
    ...calculateMatchingPlaylistBlock(
      playlistsEndTimeInMs,
      new Date(rewindEndTime).getTime(),
      blockLength,
      blockOverlap,
    ),
    ...calculateMatchingPlaylistBlock(
      playlistsEndTimeInMs,
      new Date(rewindStartTime).getTime(),
      blockLength,
      blockOverlap,
    ),
  ]),
];

export const createNewPlaylistBlock = (
  blockEndTime: string,
  blockLength: number,
): BaseRewindPlaylistBlock => {
  const bucketStart = new Date(new Date(blockEndTime).getTime() - blockLength);
  const bucketEnd = new Date(blockEndTime);

  return {
    bucketStart,
    bucketEnd,
    rewinds: [],
    errors: [],
  };
};

export const updateToSmartRewindPlaylistBlock = (
  playlistBlock: BaseRewindPlaylistBlock,
): SmartRewindPlaylistBlock => {
  const { bucketStart, bucketEnd, rewinds } = playlistBlock;

  return {
    ...playlistBlock,
    rewinds: rewinds as RewindRecording[],
    waveGraphBars: generateEmptyWaveGraphBars(bucketStart.getTime(), bucketEnd.getTime()),
  } as SmartRewindPlaylistBlock;
};

export const updateToClipsPlaylistBlock = (
  playlistBlock: BaseRewindPlaylistBlock,
): ClipsPlaylistBlock => {
  const { rewinds } = playlistBlock;

  return {
    ...playlistBlock,
    rewinds: rewinds as RewindRecording[],
    waves: [],
  } as ClipsPlaylistBlock;
};

export const transformToRewindError = ({
  mlStatus,
  isBadVisibility,
  isLowWaveCount,
}: {
  mlStatus: MLStatus;
  isBadVisibility?: boolean;
  isLowWaveCount?: boolean;
}): RewindErrorType | undefined => {
  if (mlStatus === MLStatus.PENDING) return RewindErrorType.GENERATING_INSIGHTS;
  if (mlStatus === MLStatus.FAILED) return RewindErrorType.NO_DATA;
  if (mlStatus === MLStatus.NOT_ML_CAM) return RewindErrorType.NO_DATA;
  if (mlStatus === MLStatus.NOT_RUN) return RewindErrorType.NO_WAVES_TRACKED;
  if (mlStatus === MLStatus.COMPLETE) {
    if (isBadVisibility && isLowWaveCount) return RewindErrorType.VISIBILITY_BAD_LOW_WAVES;
    if (!isBadVisibility && isLowWaveCount) return RewindErrorType.VISIBILITY_GOOD_LOW_WAVES;
    if (isBadVisibility) return RewindErrorType.VISIBILITY_BAD;
  }
  return undefined;
};

export const updatePlaylistBlockWithRewind = (
  rewind: RewindRecording,
  existingPlaylistBlock: SmartRewindPlaylistBlock | ClipsPlaylistBlock,
): SmartRewindPlaylistBlock | ClipsPlaylistBlock => {
  const updatedPlaylistBlock = cloneDeep(existingPlaylistBlock);
  const { insights, ...rewindWithoutWavesAndStills } = rewind;
  updatedPlaylistBlock.rewinds.unshift(rewindWithoutWavesAndStills);

  const rewindError = transformToRewindError({
    mlStatus: rewind.mlStatus,
    isBadVisibility: insights?.isVisibilityBad,
    isLowWaveCount: insights?.lowWaveCount,
  });

  if (rewindError) {
    updatedPlaylistBlock.errors.push({
      type: rewindError,
      // dates are clipped to the start and end of the playlist block
      // because rewinds don't always start and end at the start and end of a playlist block
      startDate: clipDate({
        date: new Date(rewind.startDate),
        start: updatedPlaylistBlock.bucketStart,
        end: updatedPlaylistBlock.bucketEnd,
      }),
      endDate: clipDate({
        date: new Date(rewind.endDate),
        start: updatedPlaylistBlock.bucketStart,
        end: updatedPlaylistBlock.bucketEnd,
      }),
    });
  }

  return updatedPlaylistBlock;
};

export const addWavesToSmartRewindPlaylistBlock = (
  rewind: RewindRecording,
  existingPlaylistBlock: BaseRewindPlaylistBlock,
): SmartRewindPlaylistBlock => {
  const updatedPlaylistBlock = cloneDeep(existingPlaylistBlock) as SmartRewindPlaylistBlock;
  const { insights } = rewind;
  const { waves } = insights || {};

  const shouldHideWaves = insights?.lowWaveCount || false;
  const shouldHideSurfedDirection = insights?.isVisibilityBad || false;

  if (waves && waves.length && !shouldHideWaves) {
    updatedPlaylistBlock.associated = rewind.associated;
    updatedPlaylistBlock.hasInsights = true;
    updatedPlaylistBlock.waveGraphBars = updateWaveGraphBarsWithNewRewind(
      updatedPlaylistBlock.waveGraphBars,
      waves,
      updatedPlaylistBlock.bucketStart.getTime(),
      shouldHideSurfedDirection,
    );
  }
  return updatedPlaylistBlock;
};

export const addWavesToClipRewindBlock = (
  rewind: RewindRecording,
  existingPlaylistBlock: BaseRewindPlaylistBlock | ClipsPlaylistBlock,
): ClipsPlaylistBlock => {
  const updatedPlaylistBlock = cloneDeep(existingPlaylistBlock) as ClipsPlaylistBlock;
  const { insights } = rewind;
  let { waves } = insights || {};

  if (updatedPlaylistBlock.waves === undefined) {
    updatedPlaylistBlock.waves = [];
  }

  const shouldHideWaves = insights?.lowWaveCount || false;
  if (shouldHideWaves || !waves?.length) return updatedPlaylistBlock;

  const shouldHideSurfedDirection = insights?.isVisibilityBad || false;

  if (shouldHideSurfedDirection) {
    waves = waves.map((wave) => ({
      ...wave,
      surfedDirection: SurfedDirection.NONE,
    }));
  }

  waves = waves
    .filter((wave) => {
      const waveStartDate = new Date(wave.startTime);
      const waveEndDate = wave.endTime && new Date(wave.endTime);
      return (
        waveStartDate >= updatedPlaylistBlock.bucketStart &&
        waveStartDate < updatedPlaylistBlock.bucketEnd &&
        wave.height > 0 &&
        waveEndDate &&
        waveEndDate.getTime() - waveStartDate.getTime() > 1000
      );
    })
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    // combine and filter waves with matching start times
    .map((wave, index, filteredWaves) => {
      const nextWave = filteredWaves[index + 1];
      if (nextWave && nextWave.startTime === wave.startTime) {
        return combineWaves(wave, nextWave);
      }
      return wave;
    })
    .filter((wave, index, filteredWaves) => {
      const previousWave = filteredWaves[index - 1];
      if (previousWave && previousWave.startTime === wave.startTime) {
        // the last wave of a set of matching waves hasn't been combined so we want to discard it
        return false;
      }
      return true;
    });

  updatedPlaylistBlock.waves.unshift(...waves);

  updatedPlaylistBlock.associated = rewind.associated;
  updatedPlaylistBlock.hasInsights = true;

  return updatedPlaylistBlock;
};

export const getStartAndEndTimesFromAllRewinds = (rewinds: RewindRecording[]) => {
  const rewindsStartTimeInMs = Math.min(
    ...rewinds
      .map(({ startDate }) => (startDate ? new Date(startDate).getTime() : NaN))
      .filter((val) => !Number.isNaN(val)),
  );

  const rewindsEndTimeDate = new Date(
    Math.max(
      ...rewinds
        .map(({ endDate }) => (endDate ? new Date(endDate).getTime() : NaN))
        .filter((val) => !Number.isNaN(val)),
    ),
  );

  rewindsEndTimeDate.setSeconds(0, 0);
  const rewindsEndTimeInMs = rewindsEndTimeDate.getTime();

  return { rewindsStartTimeInMs, rewindsEndTimeInMs };
};

const isErrorWithinEntry = (entry: BaseRewindPlaylistBlock, error: RewindError) =>
  new Date(entry.bucketStart).getTime() < new Date(entry.bucketEnd).getTime() &&
  new Date(error.endDate).getTime() > new Date(error.startDate).getTime();

export const firstPreviouslyCompletePlaylist = (
  rewindPlaylists: RewindPlaylists | undefined,
  startTime: number,
) =>
  Array.from(rewindPlaylists?.values() ?? [])
    .filter(
      (entry) =>
        new Date(entry.bucketStart).getTime() < startTime &&
        !entry.errors.some((error) => isErrorWithinEntry(entry, error)),
    )
    .find((entry) => entry.rewinds.some((rewind) => rewind.mlStatus === 'COMPLETE'));
